import { Injectable } from '@angular/core';
import { Device, Call } from '@twilio/voice-sdk';
import { BehaviorSubject } from 'rxjs';
import { ToasterMessageService } from '../../core/services/topbar-toaster/toaster-message.service';
import { DashboardService } from './dashboard.service';
import { CommonHttpsService } from '../../core/services/https/common-https.service';

// Shape of the periodic stats Call emits via 'sample' — see @twilio/voice-sdk's
// RTCSample. Declared locally since the SDK doesn't export this path publicly.
interface RTCSample {
  audioInputLevel: number;
  audioOutputLevel: number;
  bytesReceived: number;
  bytesSent: number;
  codecName: string;
  jitter: number;
  mos: number | null;
  packetsLost: number;
  packetsLostFraction: number;
  rtt: number;
  timestamp: number;
  [key: string]: any;
}

// A 'sample' tick tagged with when it was captured relative to makeCall()
// and relative to the call's 'accept' event, used to compare call-startup
// quality against the rest of the call.
interface TaggedSample {
  sample: RTCSample;
  elapsedMs: number;
  elapsedFromAcceptMs: number | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class TwilioService {
  private device?: Device;
  public device$ = new BehaviorSubject<Device | undefined>(undefined);
  public activeCall$ = new BehaviorSubject<Call | undefined>(undefined);
  private activeCall?: Call;
  private callData: {
    sid: string;
    duration: number;
  } = { sid: '', duration: 0 };
  public latestCallStats$ = new BehaviorSubject<any>(undefined);
  private callSamples: TaggedSample[] = [];
  private callWarnings: { name: string; data: any; timestamp: number }[] = [];
  private callStartedAt = 0;

  // Diagnostics-only state for the current call — reset at the start of each
  // makeCall(). A single performance.now() reference per call, not a new
  // timer per lifecycle event.
  private callAcceptedAt: number | undefined;

  private static readonly STARTUP_WINDOW_MS = 15000;

  // Mic pre-warm: open the mic when the call dialog opens instead of at dial
  // time. Bluetooth headsets (A2DP -> HFP switch) and VDI-redirected mics can
  // take seconds to start delivering audio, so the first words of a call
  // were lost. With an input device set, the SDK keeps one stream open and
  // each Call clones its tracks, so per-call mute/teardown don't touch it.
  // Set to false to disable without reverting (see fallbacks/mic-prewarm).
  private static readonly MIC_PREWARM_ENABLED = true;
  // Safety net: release a warmed mic if no call is placed within this time.
  private static readonly MIC_PREWARM_IDLE_MS = 120000;
  private micWarm = false;
  private micWarmPromise: Promise<void> | null = null;
  private micReleaseTimer: any = null;
  private callInProgress = false;

  private logDeviceState(label: string): void {
    console.log(`[TWILIO DEVICE] ${label}`, {
      state: this.device?.state,
      edge: (this.device as any)?.edge,
      isRegistered: this.device?.state === 'registered',
    });
  }

  constructor(
    public messageService: ToasterMessageService,
    private dashboardService: DashboardService,
    public commonhttpService: CommonHttpsService,
  ) {}

  setActiveCall(call: Call | undefined) {
    this.activeCall = call;
    this.activeCall$.next(call);
  }

  getActiveCall() {
    return this.activeCall;
  }

  getDevice() {
    return this.device;
  }

  getCallData() {
    return this.callData;
  }

  setCallData(sid: string, duration: number) {
    this.callData.sid = sid;
    this.callData.duration = duration;
  }

  sendDigit(digit: string) {
    if (this.activeCall) {
      console.log('Sending digit via TwilioService:', digit);
      this.activeCall.sendDigits(digit);
    } else {
      console.warn('No active call to send digit');
    }
  }

  toggleMute(mute: boolean) {
    if (this.activeCall) {
      console.log('Toggling mute state:', mute);
      this.activeCall.mute(mute);
    } else {
      console.warn('No active call to mute/unmute');
    }
  }

  // Called when a call dialog opens. Failures are non-fatal — the call just
  // falls back to the SDK opening the mic itself at dial time.
  warmUpMic(): Promise<void> {
    const audio = this.device?.audio;
    if (!TwilioService.MIC_PREWARM_ENABLED || !audio) {
      return Promise.resolve();
    }
    this.clearMicReleaseTimer();
    if (this.micWarm) {
      this.scheduleMicRelease(TwilioService.MIC_PREWARM_IDLE_MS);
      return Promise.resolve();
    }
    if (this.micWarmPromise) {
      return this.micWarmPromise;
    }

    const startedAt = performance.now();
    this.micWarmPromise = audio
      .setInputDevice('default')
      .then(() => {
        this.micWarm = true;
        console.log(
          `[TWILIO MIC] pre-warmed in ${Math.round(performance.now() - startedAt)} ms`,
        );
        if (!this.callInProgress) {
          this.scheduleMicRelease(TwilioService.MIC_PREWARM_IDLE_MS);
        }
      })
      .catch((error: any) => {
        console.warn('[TWILIO MIC] pre-warm failed, mic will open at dial time', error);
      })
      .finally(() => {
        this.micWarmPromise = null;
      });
    return this.micWarmPromise;
  }

  // Release the warmed mic after a delay (e.g. call dialog closed). Skipped
  // while a call is being placed or is live — it's released on disconnect.
  scheduleMicRelease(delayMs = 5000): void {
    this.clearMicReleaseTimer();
    this.micReleaseTimer = setTimeout(() => {
      this.micReleaseTimer = null;
      void this.releaseMic();
    }, delayMs);
  }

  private clearMicReleaseTimer(): void {
    if (this.micReleaseTimer) {
      clearTimeout(this.micReleaseTimer);
      this.micReleaseTimer = null;
    }
  }

  private async releaseMic(): Promise<void> {
    this.clearMicReleaseTimer();
    if (this.micWarmPromise) {
      await this.micWarmPromise;
    }
    const audio = this.device?.audio;
    if (!this.micWarm || !this.device || !audio) return;
    // unsetInputDevice() rejects while a call is using the stream.
    if (this.callInProgress || this.device.isBusy) return;
    try {
      await audio.unsetInputDevice();
      console.log('[TWILIO MIC] released');
    } catch (error) {
      console.warn('[TWILIO MIC] release failed', error);
    }
    this.micWarm = false;
  }

  initialize(token: any) {
    // Guard against redundant re-init: every page that needs a ready Device
    // (main-layout at login, profile-view on opening a patient) calls this.
    // Without this guard, each call created a brand-new Device on top of the
    // still-live previous one, which was never destroyed — leaking a
    // registered Device (its own signaling socket/heartbeat) per patient
    // visited in a shift. Harmless on a machine with CPU to spare, but the
    // accumulated background load was enough to degrade live call audio on
    // more resource-constrained sessions (e.g. VDI).
    if (
      this.device &&
      (this.device.state === 'registered' ||
        this.device.state === 'registering')
    ) {
      console.log('ℹ️ Twilio Device already initialized — skipping re-init');
      this.device$.next(this.device);
      return;
    }

    try {
      console.log('Creating Twilio Device...');
      const deviceOptions = {
        allowIncomingWhileBusy: true,
        // Prefer Opus (variable bitrate, better loss/jitter resilience) over PCMU
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        // Mark outgoing RTP for QoS prioritization where the network honors DSCP.
        dscp: true,
        // NOTE: previously also set maxAverageBitrate: 16000 and disabled
        // autoGainControl/noiseSuppression here as jitter mitigations. Reverted —
        // on a VDI virtual/redirected mic we don't control the input level of,
        // an aggressive bitrate cap and disabled AGC are plausible sources of
        // audible compression artifacts/clipping themselves, and this Device
        // config is the one concrete difference between the call path that
        // distorts (this service) and the one that doesn't (a separate Twilio
        // service elsewhere in the app that uses plain SDK defaults). Twilio's
        // own call-quality stats (jitter/RTT/loss/MOS) can't detect this kind
        // of source-side artifact, which is why they looked clean throughout.
      };
      this.device = new Device(token, deviceOptions);
      // publish device so consumers can attach listeners when available
      this.device$.next(this.device);

      this.device.on('registering', () => {
        console.log('ℹ️ Registering Twilio Device...');
        this.logDeviceState('registering');
      });

      this.device.register();

      console.log(this.device);

      this.device.on('registered', () => {
        console.log('✅ Twilio Device registered');
        localStorage.setItem('deviceRegistered', 'true');
        this.logDeviceState('registered');
        // this.messageService.successMessage('Twilio Device registered', true);
      });

      this.device.on('incoming', (call: Call) => {
        console.log('📞 Incoming call:', call);
        // this.messageService.infoMessage('Incoming call', true);
      });

      this.device.on('error', (error: any) => {
        console.error('❌ Twilio Device error:', error);
        localStorage.setItem('deviceRegistered', 'false');
        this.logDeviceState('device error');
        // this.messageService.errroMessage('Twilio Device error', true);
      });

      // this.device.on('incoming', (call) => {
      //   console.log('📞 Incoming call');
      //   call.accept();
      // });

      this.device.on('ready', () => {
        console.log('✅ Twilio Device ready for calls');
        localStorage.setItem('deviceRegistered', 'true');
        // this.messageService.successMessage(
        //   '✅ Twilio Device ready for calls',
        //   true,
        // );
      });
    } catch (error) {
      console.error('🚫 Failed to initialize Twilio Device:', error);
      localStorage.setItem('deviceRegistered', 'false');
      this.device$.next(undefined);
      // this.messageService.errroMessage(
      //   '🚫 Failed to initialize Twilio Device',
      //   true,
      // );
    }
  }

  // device.connect() before the SDK's async 'registered' event has fired
  // silently fails to place the call — this waits (briefly) for that event
  // instead of trusting device truthiness alone.
  private waitForRegistration(timeoutMs = 8000): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.device) {
        resolve(false);
        return;
      }
      if (this.device.state === 'registered') {
        resolve(true);
        return;
      }

      const device = this.device;
      const onRegistered = () => {
        clearTimeout(timer);
        resolve(true);
      };
      const timer = setTimeout(() => {
        device.off('registered', onRegistered);
        resolve(false);
      }, timeoutMs);
      device.once('registered', onRegistered);
    });
  }

  async makeCall(
    params: Record<string, string> = {},
    patientId?: any,
    getConfID?: boolean,
  ): Promise<Call | undefined> {
    // Single timing reference for this call's whole lifecycle — every
    // "elapsed" log below measures from this same performance.now() call,
    // not a fresh timer per event.
    const startedAt = performance.now();
    const elapsed = () => Math.round(performance.now() - startedAt);

    console.log(`[TWILIO] ${elapsed()} ms - MAKECALL STARTED`, { params, patientId });

    if (!this.device) {
      console.error('⚠️ Twilio Device is not initialized');
      return undefined;
    }

    this.callInProgress = true;
    this.clearMicReleaseTimer();
    // If the dialog's pre-warm is still opening the mic, let it finish so
    // the call picks up the warmed stream instead of opening a second one.
    if (this.micWarmPromise) {
      await this.micWarmPromise;
    }

    if (this.device.state !== 'registered') {
      console.log(`[TWILIO] ${elapsed()} ms - WAITING FOR REGISTRATION`);
      this.logDeviceState('waiting for registration');
      const registered = await this.waitForRegistration();
      if (!registered) {
        console.error('⚠️ Twilio Device did not finish registering in time');
        this.messageService.errroMessage(
          'Phone system is still connecting. Please wait a moment and try again.',
          true,
        );
        this.callInProgress = false;
        this.scheduleMicRelease(TwilioService.MIC_PREWARM_IDLE_MS);
        return undefined;
      }
      console.log(`[TWILIO] ${elapsed()} ms - DEVICE REGISTERED`);
      this.logDeviceState('registered (post-wait)');
    }

    try {
      console.log(`[TWILIO] ${elapsed()} ms - CONNECT STARTED`, 'device.state:', this.device.state, 'params:', params);
      this.logDeviceState('connect started');
      const call: Call = await this.device.connect({ params });
      console.log('📞 [TwilioService] device.connect() resolved — call.parameters:', (call as any)?.parameters, 'call.status():', call.status());

      this.callSamples = [];
      this.callWarnings = [];
      this.callStartedAt = Date.now();
      this.callAcceptedAt = undefined;

      call.on('ringing', () => {
        console.log(`[TWILIO] ${elapsed()} ms - RINGING`);
      });

      call.on('accept', (func: any) => {
        this.callAcceptedAt = performance.now();
        console.log(`[TWILIO] ${elapsed()} ms - ACCEPTED`, func);
        this.logDeviceState('accepted');

        localStorage.setItem('callActive', 'true');
        // keep a reference to the active call so other components can access it
        this.activeCall = call;
        this.activeCall$.next(call);
        if (patientId) {
          // if (!getConfID) {
          //   return;
          // }
          // setTimeout(() => {
          //   this.dashboardService
          //     .GetConferenceId(patientId)
          //     .subscribe((data: any) => {
          //       if (typeof (this as any).onConferenceIdFetched === 'function') {
          //         (this as any).onConferenceIdFetched(data.conferenceId);
          //       }
          //     });
          // }, 3400);
        } else {
          console.warn('No patientId provided for GetConferenceId');
        }
        //https://twilioapidev.carevance.solutions/api/GetConferenceId?patientId=6666
      });

      call.on('reconnecting', (error: any) => {
        console.warn(`[TWILIO] ${elapsed()} ms - RECONNECTING`, error);
        this.logDeviceState('reconnecting');
      });

      call.on('reconnected', () => {
        console.log(`[TWILIO] ${elapsed()} ms - RECONNECTED`);
        this.logDeviceState('reconnected');
      });

      call.on('disconnect', () => {
        console.log(`[TWILIO] ${elapsed()} ms - DISCONNECT`);
        localStorage.setItem('callActive', 'false');
        this.logCallSummary(call);
        this.logStartupVsRestSummary();
        // clear activeCall reference
        this.activeCall = undefined;
        this.activeCall$.next(undefined);
        this.latestCallStats$.next(undefined);
        this.callInProgress = false;
        this.scheduleMicRelease(1000);
        // NOTE: device.disconnectAll() intentionally removed from here — the
        // 'disconnect' event means this call has already disconnected;
        // calling disconnectAll() on the still-live singleton Device here
        // has no call left to disconnect and risks affecting a different
        // call if one were ever placed concurrently. disconnectAll() stays
        // only in the explicit hangup path (disconnectCall()).
      });

      call.on('error', (err: any) => {
        console.log(`[TWILIO] ${elapsed()} ms - ERROR`, err);
        localStorage.setItem('callActive', 'false');
        console.error('❗ Call error:', err);
        this.logCallSummary(call);
        this.logStartupVsRestSummary();
        this.activeCall = undefined;
        this.activeCall$.next(undefined);
        this.latestCallStats$.next(undefined);
        this.callInProgress = false;
        // releaseMic() skips if the Device is still busy with this call;
        // the 'disconnect' handler above schedules it again in that case.
        this.scheduleMicRelease(1000);
      });

      call.on('warning', (warningName: string, warningData: any) => {
        console.warn(`[TWILIO WARNING]`, {
          elapsedMs: elapsed(),
          name: warningName,
          data: warningData,
          deviceState: this.device?.state,
        });
        this.callWarnings.push({
          name: warningName,
          data: warningData,
          timestamp: Date.now(),
        });
      });

      call.on('warning-cleared', (warningName: string) => {
        console.info(`[TWILIO] ${elapsed()} ms - WARNING-CLEARED`, warningName);
      });

      call.on('sample', (sample: RTCSample) => {
        this.latestCallStats$.next(sample);
        const elapsedMs = elapsed();
        const elapsedFromAcceptMs = this.callAcceptedAt
          ? Math.round(performance.now() - this.callAcceptedAt)
          : undefined;
        this.callSamples.push({ sample, elapsedMs, elapsedFromAcceptMs });

        // Verbose per-sample logging only for the first 15s of the call, so
        // long calls don't flood the console.
        if (elapsedMs <= TwilioService.STARTUP_WINDOW_MS) {
          console.log('[TWILIO SAMPLE]', {
            elapsedMs,
            elapsedFromAcceptMs,
            codec: sample.codecName,
            jitter: sample.jitter,
            rtt: sample.rtt,
            packetLoss: sample.packetsLostFraction,
            packetsLost: sample.packetsLost,
            mos: sample.mos,
            audioInputLevel: sample.audioInputLevel,
            audioOutputLevel: sample.audioOutputLevel,
            bytesSent: sample.bytesSent,
            bytesReceived: sample.bytesReceived,
          });
        }
      });

      return call;
    } catch (error) {
      localStorage.setItem('callActive', 'false');
      console.error('🚫 Failed to make call:', error);
      this.callInProgress = false;
      this.scheduleMicRelease(1000);
      return undefined;
    }
  }

  // Prints a call-quality report to the console when a call ends — averages
  // jitter/RTT/packet loss/MOS across every 'sample' tick plus every warning
  // that fired, so a specific bad call (e.g. a VDI test) can be diagnosed
  // from devtools/logs without needing Twilio Console access.
  private logCallSummary(call: Call): void {
    const durationSec = this.callStartedAt
      ? Math.round((Date.now() - this.callStartedAt) / 1000)
      : undefined;
    const samples = this.callSamples.map((s) => s.sample);

    if (!samples.length) {
      console.log(
        `📊 [CallSummary] callSid=${call.parameters?.['CallSid'] ?? 'unknown'} duration=${durationSec}s — no samples collected (call likely too short)`,
      );
      return;
    }

    const avg = (fn: (s: RTCSample) => number) =>
      Math.round(
        (samples.reduce((sum, s) => sum + fn(s), 0) / samples.length) * 100,
      ) / 100;
    const max = (fn: (s: RTCSample) => number) =>
      Math.max(...samples.map(fn));
    const mosValues = samples
      .map((s) => s.mos)
      .filter((m): m is number => m !== null && m !== undefined);

    const summary = {
      callSid: call.parameters?.['CallSid'] ?? 'unknown',
      durationSec,
      codec: samples[samples.length - 1]?.codecName,
      avgJitterMs: avg((s) => s.jitter),
      maxJitterMs: max((s) => s.jitter),
      avgRttMs: avg((s) => s.rtt),
      maxRttMs: max((s) => s.rtt),
      avgPacketsLostFractionPct: avg((s) => s.packetsLostFraction * 100),
      totalPacketsLost: samples.reduce((sum, s) => sum + s.packetsLost, 0),
      avgMos: mosValues.length
        ? Math.round((mosValues.reduce((a, b) => a + b, 0) / mosValues.length) * 100) / 100
        : null,
      minMos: mosValues.length ? Math.min(...mosValues) : null,
      warningCount: this.callWarnings.length,
      warnings: this.callWarnings,
    };

    console.log(`📊 [CallSummary] Call ${summary.callSid} performance report`);
    console.table({
      'Duration (s)': summary.durationSec,
      Codec: summary.codec,
      'Avg Jitter (ms)': summary.avgJitterMs,
      'Max Jitter (ms)': summary.maxJitterMs,
      'Avg RTT (ms)': summary.avgRttMs,
      'Max RTT (ms)': summary.maxRttMs,
      'Avg Packet Loss (%)': summary.avgPacketsLostFractionPct,
      'Total Packets Lost': summary.totalPacketsLost,
      'Avg MOS (1-4.5)': summary.avgMos,
      'Min MOS (1-4.5)': summary.minMos,
      'Warning Count': summary.warningCount,
    });
    if (summary.warnings.length) {
      console.log('⚠️ Warnings during call:', summary.warnings);
    }
  }

  // Compares call quality in the first STARTUP_WINDOW_MS after 'accept'
  // against the remainder of the call. The aggregate summary above can look
  // clean even when the call was distorted/jittery for the first several
  // seconds and then recovered — this splits the same sample data by time
  // relative to accept so that startup-only degradation isn't averaged away.
  private logStartupVsRestSummary(): void {
    if (!this.callAcceptedAt) {
      console.log(
        '[TWILIO STARTUP QUALITY] No accept timestamp recorded — call never connected, skipping startup comparison.',
      );
      return;
    }

    const withAcceptOffset = this.callSamples.filter(
      (s): s is TaggedSample & { elapsedFromAcceptMs: number } =>
        s.elapsedFromAcceptMs !== undefined,
    );

    if (!withAcceptOffset.length) {
      console.log(
        '[TWILIO STARTUP QUALITY] No samples captured after accept — call likely too short.',
      );
      return;
    }

    const startupSamples = withAcceptOffset.filter(
      (s) => s.elapsedFromAcceptMs <= TwilioService.STARTUP_WINDOW_MS,
    );
    const restSamples = withAcceptOffset.filter(
      (s) => s.elapsedFromAcceptMs > TwilioService.STARTUP_WINDOW_MS,
    );

    const summarize = (tagged: TaggedSample[]) => {
      const samples = tagged.map((t) => t.sample);
      if (!samples.length) {
        return {
          samples: 0,
          avgJitterMs: null,
          maxJitterMs: null,
          avgRttMs: null,
          maxRttMs: null,
          avgPacketLossFraction: null,
          avgMos: null,
          minMos: null,
          avgAudioInputLevel: null,
          avgAudioOutputLevel: null,
        };
      }
      const round2 = (n: number) => Math.round(n * 100) / 100;
      const avg = (fn: (s: RTCSample) => number) =>
        round2(samples.reduce((sum, s) => sum + fn(s), 0) / samples.length);
      const max = (fn: (s: RTCSample) => number) => Math.max(...samples.map(fn));
      const mosValues = samples
        .map((s) => s.mos)
        .filter((m): m is number => m !== null && m !== undefined);

      return {
        samples: samples.length,
        avgJitterMs: avg((s) => s.jitter),
        maxJitterMs: max((s) => s.jitter),
        avgRttMs: avg((s) => s.rtt),
        maxRttMs: max((s) => s.rtt),
        avgPacketLossFraction: avg((s) => s.packetsLostFraction),
        avgMos: mosValues.length
          ? round2(mosValues.reduce((a, b) => a + b, 0) / mosValues.length)
          : null,
        minMos: mosValues.length ? Math.min(...mosValues) : null,
        avgAudioInputLevel: avg((s) => s.audioInputLevel),
        avgAudioOutputLevel: avg((s) => s.audioOutputLevel),
      };
    };

    console.log('[TWILIO STARTUP QUALITY] First 15 seconds after accept:');
    console.table(summarize(startupSamples));

    console.log('[TWILIO POST-STARTUP QUALITY] After first 15 seconds:');
    console.table(summarize(restSamples));
  }

  disconnectCall() {
    this.device?.disconnectAll();
    localStorage.setItem('callActive', 'false');
    this.activeCall = undefined;
    this.activeCall$.next(undefined);
  }

  destroy() {
    this.clearMicReleaseTimer();
    this.callInProgress = false;
    this.micWarm = false;
    this.device?.destroy();
    localStorage.setItem('callActive', 'false');
    this.device$.next(undefined);
    this.activeCall = undefined;
    this.activeCall$.next(undefined);
  }
}